import { createBrowserRouter, useLocation, useNavigate, useRouteError } from 'react-router'
import { AppErrorPage } from '@/pages/app-error'
import { ROUTES } from '@/shared/config'
import { AppLayout } from '@/widgets/app-layout'
import { queryClient } from './providers/query-client'

/** Ошибка экрана: перерисовывается только экран, оболочка и BackButton живут дальше. */
const RouteError = () => {
  const error = useRouteError()
  const navigate = useNavigate()
  const location = useLocation()

  const retry = async () => {
    // Упавшие запросы — заново, а не из кэша ошибки.
    await queryClient.resetQueries({ predicate: (query) => query.state.status === 'error' })
    // Новая навигация на тот же адрес снимает ошибку маршрута без перезагрузки WebView.
    navigate(`${location.pathname}${location.search}`, { replace: true, state: location.state })
  }

  return <AppErrorPage error={error} onRetry={retry} />
}

const EmptyFallback = () => null

/** Экран ваучера с кодированием QR (≈14 КБ gzip) — отдельный чанк, см. маршруты подарка. */
const loadVoucherPage = () => import('@/pages/voucher')

/** Экран решения (10, 13, 14) — следующий после коробки; догружается, пока она на экране. */
const loadDecisionPage = () => import('@/pages/gift-decision')

const loadUnwrapPage = () => import('@/pages/unwrap')

/**
 * Маршруты ленивые: первый экран не ждёт кода остальных.
 *
 * Роутер создаётся при первом рендере, а не при загрузке модуля: до него
 * `app/launch.ts` успевает переписать адрес по `start_param` (вход из чата
 * сразу на подарок), и роутер стартует уже с нужного экрана — код витрины при
 * этом не грузится вовсе.
 */
export const createAppRouter = () =>
  createBrowserRouter([
    {
      element: <AppLayout />,
      errorElement: <RouteError />,
      // Пока грузится чанк первого экрана, роутер и так ничего не рисует; явный
      // пустой fallback только снимает предупреждение React Router в консоли.
      HydrateFallback: EmptyFallback,
      children: [
        {
          errorElement: <RouteError />,
          children: [
            {
              path: ROUTES.CATALOG,
              lazy: () => import('@/pages/catalog').then((module) => ({ Component: module.CatalogPage })),
            },
            {
              path: ROUTES.BRAND,
              lazy: () => import('@/pages/partner').then((module) => ({ Component: module.PartnerPage })),
            },
            {
              path: ROUTES.CHECKOUT,
              lazy: () => import('@/pages/checkout').then((module) => ({ Component: module.CheckoutPage })),
            },
            {
              path: ROUTES.PAY,
              lazy: () => import('@/pages/pay').then((module) => ({ Component: module.PayPage })),
            },
            {
              path: ROUTES.ORDER,
              lazy: () => import('@/pages/pay-result').then((module) => ({ Component: module.PayResultPage })),
            },
            {
              // Распаковка и всё, что ей нужно (коробка, конфетти), — в чанке этого
              // маршрута: грузится при входе на подарок, а не со стартом приложения.
              path: ROUTES.GIFT_SEND,
              lazy: async () => {
                const module = await loadUnwrapPage()

                // Следом — чанк экрана решения: пока человек смотрит на коробку, он
                // догружается, и после карточки-сюрприза экран въезжает без ожидания.
                void loadDecisionPage()

                return { Component: module.UnwrapPage }
              },
            },
            {
              // Вход по id: решение, ваучер или исход — без коробки.
              path: ROUTES.GIFT,
              lazy: () => loadUnwrapPage().then((module) => ({ Component: module.GiftEntryPage })),
            },
            {
              path: ROUTES.GIFT_DECISION,
              lazy: async () => {
                const module = await loadDecisionPage()

                // После «Принять» — ваучер (QR): его чанк догружается, пока человек читает поздравление.
                void loadVoucherPage()

                return { Component: module.GiftDecisionPage }
              },
            },
            {
              path: ROUTES.VOUCHER,
              lazy: () => loadVoucherPage().then((module) => ({ Component: module.VoucherPage })),
            },
            {
              // «Где принимают» (11–12) — с решения и с ваучера; «Назад» — туда, откуда открыли.
              path: ROUTES.GIFT_LOCATIONS,
              lazy: () => import('@/pages/locations').then((module) => ({ Component: module.LocationsPage })),
            },
            {
              // Ролики сторис грузятся только здесь, при открытии, — не со стартом приложения.
              path: ROUTES.STORIES,
              lazy: () => import('@/pages/stories').then((module) => ({ Component: module.StoriesPage })),
            },
            {
              path: ROUTES.ONBOARDING,
              lazy: () => import('@/pages/onboarding').then((module) => ({ Component: module.OnboardingPage })),
            },
            {
              path: ROUTES.PROFILE,
              lazy: () => import('@/pages/profile').then((module) => ({ Component: module.ProfilePage })),
            },
            {
              path: ROUTES.MY_GIFTS,
              lazy: () => import('@/pages/my-gifts').then((module) => ({ Component: module.MyGiftsPage })),
            },
            {
              path: ROUTES.HISTORY,
              lazy: () => import('@/pages/history').then((module) => ({ Component: module.HistoryPage })),
            },
            {
              path: ROUTES.LANGUAGE,
              lazy: () => import('@/pages/language').then((module) => ({ Component: module.LanguagePage })),
            },
            {
              path: ROUTES.SETTINGS,
              lazy: () => import('@/pages/settings').then((module) => ({ Component: module.SettingsPage })),
            },
            {
              // Форма, список и обращение — один чанк: из формы почти всегда идут в обращение.
              path: ROUTES.SUPPORT,
              lazy: () => import('@/pages/support').then((module) => ({ Component: module.SupportPage })),
            },
            {
              path: ROUTES.SUPPORT_TICKETS,
              lazy: () => import('@/pages/support').then((module) => ({ Component: module.TicketListPage })),
            },
            {
              path: ROUTES.SUPPORT_TICKET,
              lazy: () => import('@/pages/support').then((module) => ({ Component: module.TicketPage })),
            },
            {
              path: ROUTES.NOT_FOUND,
              lazy: () => import('@/pages/not-found').then((module) => ({ Component: module.NotFoundPage })),
            },
          ],
        },
      ],
    },
  ])
